'use client';

import { useTranslations } from 'next-intl';
import { formatPrice } from '@/lib/format-price';

interface PriceSummaryProps {
  subtotal: number;
  /**
   * Discounts as Magento reports them, each with the name of the rule that
   * fired. Preferred over `discount`, because the rule's own name is what tells
   * the customer WHY the price dropped — "Paketrabatt" rather than "Rabatt".
   */
  discounts?: { amount: number; label?: string }[];
  /**
   * Total discount as a single figure, for callers that only have the sum (the
   * account order detail). Ignored when `discounts` is supplied. The Rabatt
   * line is hidden when 0/undefined.
   */
  discount?: number;
  /** Shipping cost; the Frakt line is hidden when undefined. */
  shipping?: number;
  /** Shown instead of a price when shipping is not yet known — the cart's "Beräknas i kassan". */
  shippingNote?: string;
  /**
   * VAT total. The row renders whenever this is a number, 0 included, so a cart of goods without
   * VAT (used goods) reads "0 kr". Undefined means the caller has no tax info and gets no row.
   */
  tax?: number;
  /**
   * Whether `subtotal` already contains `tax`.
   *
   * The cart and checkout display prices including VAT, matching the store's own settings
   * (`tax/cart_display/subtotal = 2`), so there the VAT row is a breakdown of a figure the
   * customer has already seen rather than something added to it — Swedish receipts write that
   * as "varav moms". Left false for callers whose subtotal excludes tax, where the row really
   * is an addend and reads simply "Moms".
   */
  taxIncludedInSubtotal?: boolean;
  total: number;
  currency: string;
  freeShipping?: boolean;
  /**
   * `lg` draws the canvas's summary-panel total: 26px, display face, in
   * `--color-price`. That token rather than `text-accent-foreground` because it
   * is the one the theme reserves for large price text — accent-600 only clears
   * AA at this size, which is exactly the case here.
   *
   * The default stays modest for the places with no canvas screen behind them —
   * the mini-cart drawer footer and the account order detail — where that size
   * would outweigh everything around it.
   */
  totalSize?: 'md' | 'lg';
}

/**
 * The shared Delsumma / Rabatt / Frakt / Moms / Totalt breakdown. Used by the
 * mini-cart drawer, cart page, checkout, and order-detail page so every view
 * shows the same lines, in the same order, with the same (cart-namespace) labels.
 *
 * Formats through lib/format-price rather than the design system's, for the
 * reason that module exists: Torsbo's catalogue has no öre, and a card reading
 * "23 995 kr" beside a cart reading "23 995,00 kr" looks like two prices.
 */
export function PriceSummary({
  subtotal,
  discounts,
  discount,
  shipping,
  shippingNote,
  tax,
  taxIncludedInSubtotal = false,
  total,
  currency,
  freeShipping,
  totalSize = 'md',
}: PriceSummaryProps) {
  const t = useTranslations('cart');

  return (
    <div className="flex flex-col gap-3">
      <Row label={t('subtotal')} value={formatPrice(subtotal, currency)} />

      {/* One row per rule when we have them, so a package deal reads
          "Paketrabatt" and a coupon reads its own code. Magento allows a rule
          with no label, hence the fallback. */}
      {discounts && discounts.length > 0
        ? discounts
            .filter((line) => line.amount > 0)
            .map((line, index) => (
              <Row
                key={`${line.label ?? 'discount'}-${index}`}
                label={line.label?.trim() || t('discount')}
                value={`-${formatPrice(line.amount, currency)}`}
                valueClassName="text-success"
              />
            ))
        : discount !== undefined &&
          discount > 0 && (
            <Row
              label={t('discount')}
              value={`-${formatPrice(discount, currency)}`}
              valueClassName="text-success"
            />
          )}

      {shipping !== undefined ? (
        <Row
          label={t('shipping')}
          value={freeShipping || shipping === 0 ? t('freeShipping') : formatPrice(shipping, currency)}
        />
      ) : (
        shippingNote && <Row label={t('shipping')} value={shippingNote} />
      )}

      {tax !== undefined && (
        <Row
          label={taxIncludedInSubtotal ? t('taxIncluded') : t('tax')}
          value={formatPrice(tax, currency)}
        />
      )}

      <div className="flex items-baseline justify-between border-t border-border pt-3">
        <span className="text-[15px] font-bold text-foreground">{t('total')}</span>
        <span
          className={
            totalSize === 'lg'
              ? 'font-heading text-[26px] font-bold leading-none text-price'
              : 'font-semibold text-foreground'
          }
        >
          {formatPrice(total, currency)}
        </span>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-foreground/70">{label}</span>
      <span className={valueClassName ?? 'text-foreground/70'}>{value}</span>
    </div>
  );
}
